/**
 * Safety Panel
 * 
 * Critical safety configuration for sessions:
 * - Safe word (hard stop)
 * - Slow word (reduce intensity)
 * - Aftercare notes
 * 
 * Per spec: Safe word is inherited by JOI Engine and Lust Witch sessions.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronDown, ChevronUp, Save, Loader2, Check, AlertTriangle, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/api';

function SafetyPanel({
    personaId,
    initialData = {},
    onSave,
    collapsible = true,
    defaultExpanded = false
}) {
    const { getAuthHeaders } = useAuth();

    const [expanded, setExpanded] = useState(defaultExpanded);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({
        safe_word: initialData.safe_word || '',
        slow_word: initialData.slow_word || '',
        aftercare_notes: initialData.aftercare_notes || ''
    });

    // Sync with initialData when it changes
    useEffect(() => {
        setForm({
            safe_word: initialData.safe_word || '',
            slow_word: initialData.slow_word || '',
            aftercare_notes: initialData.aftercare_notes || ''
        });
    }, [initialData]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setSaved(false);
    };

    const handleSave = async () => {
        if (!personaId) return;

        setSaving(true);
        setError(null);

        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/api/profiles/persona/${personaId}/safety`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                },
                body: JSON.stringify(form)
            });

            if (!response.ok) {
                throw new Error('Failed to save safety settings');
            }

            setSaved(true);
            setTimeout(() => setSaved(false), 3000);

            if (onSave) {
                onSave(form);
            }
        } catch (err) {
            console.error('Save error:', err);
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = JSON.stringify(form) !== JSON.stringify({
        safe_word: initialData.safe_word || '',
        slow_word: initialData.slow_word || '',
        aftercare_notes: initialData.aftercare_notes || ''
    });

    const hasSafeWord = !!form.safe_word;

    const content = (
        <div className="space-y-4 pt-4">
            {/* Important notice */}
            <div className="p-3 bg-amber-900/20 border border-amber-500/30 text-amber-200 text-sm flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 mt-0.5 text-amber-400 flex-shrink-0" />
                <div>
                    <strong>Your safe word is respected everywhere.</strong>
                    <p className="text-amber-200/70 mt-1">
                        JOI Engine sessions, Lust Witch tasks, and partner activities will all recognize and honor your safe word immediately.
                    </p>
                </div>
            </div>

            {/* Safe Word */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2">
                    Safe Word <span className="text-red-400">*</span>
                </label>
                <input
                    type="text"
                    value={form.safe_word}
                    onChange={(e) => handleChange('safe_word', e.target.value)}
                    placeholder="e.g., Red, Pineapple, Stop..."
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm placeholder:text-dark-500"
                />
                <p className="text-xs text-dark-400 mt-1">
                    Saying this word stops all activity immediately. Choose something memorable but unlikely to say accidentally.
                </p>
            </div>

            {/* Slow Word */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2">
                    Slow Word (Optional)
                </label>
                <input
                    type="text"
                    value={form.slow_word}
                    onChange={(e) => handleChange('slow_word', e.target.value)}
                    placeholder="e.g., Yellow, Mercy, Slow down..."
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm placeholder:text-dark-500"
                />
                <p className="text-xs text-dark-400 mt-1">
                    Signals to reduce intensity without stopping completely. Useful for edge play.
                </p>
            </div>

            {/* Aftercare Notes */}
            <div>
                <label className="block text-xs uppercase tracking-wider text-cream/50 mb-2 flex items-center gap-2">
                    <Heart className="w-3.5 h-3.5 text-pink-400" />
                    Aftercare Preferences
                </label>
                <textarea
                    value={form.aftercare_notes}
                    onChange={(e) => handleChange('aftercare_notes', e.target.value)}
                    placeholder="What helps you feel cared for after intense experiences? e.g., physical comfort, quiet time, snacks, reassurance..."
                    rows={4}
                    className="w-full bg-charcoal-950 text-cream px-3 py-2 border border-wine-700/50 focus:border-wine-500 outline-none text-sm placeholder:text-dark-500 resize-none"
                />
                <p className="text-xs text-dark-400 mt-1">
                    These notes can be shared with partners to help them support you.
                </p>
            </div>

            {/* Error */}
            {error && (
                <div className="p-3 bg-red-900/20 border border-red-500/30 text-red-400 text-sm">
                    {error}
                </div>
            )}

            {/* Save Button */}
            <div className="pt-2">
                <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    className={`
                        flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all
                        ${hasChanges
                            ? 'bg-wine-600 hover:bg-wine-500 text-white'
                            : 'bg-dark-700 text-dark-400 cursor-not-allowed'
                        }
                        ${saved ? 'bg-green-600 hover:bg-green-600' : ''}
                    `}
                >
                    {saving ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                    ) : saved ? (
                        <><Check className="w-4 h-4" /> Saved</>
                    ) : (
                        <><Save className="w-4 h-4" /> Save Safety Settings</>
                    )}
                </button>
            </div>
        </div>
    );

    if (!collapsible) {
        return (
            <div className="glass-card p-6 border border-wine-700/30">
                <h3 className="text-lg font-display font-bold text-cream flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-400" />
                    Safety & Aftercare
                </h3>
                {content}
            </div>
        );
    }

    return (
        <div className="glass-card border border-wine-700/30 overflow-hidden">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-wine-950/20 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-emerald-400" />
                    <span className="font-display font-bold text-cream">Safety & Aftercare</span>
                    {hasSafeWord ? (
                        <span className="text-xs px-2 py-0.5 bg-emerald-900/30 text-emerald-300 flex items-center gap-1">
                            <Check className="w-3 h-3" /> Configured
                        </span>
                    ) : (
                        <span className="text-xs px-2 py-0.5 bg-amber-900/30 text-amber-300">
                            Not set
                        </span>
                    )}
                </div>
                {expanded ? (
                    <ChevronUp className="w-5 h-5 text-cream/40" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-cream/40" />
                )}
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 border-t border-wine-700/20">
                            {content}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default SafetyPanel;
